"use client";

import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import type { ExternalToast, ToastClassnames, ToasterProps } from "sonner";

type SonnerToast = typeof sonnerToast;
type ToastMessage = Parameters<SonnerToast["message"]>[0];
type ToastOptions = ExternalToast | undefined;
type ToastIcons = NonNullable<ToasterProps["icons"]>;

type NotificationVariant = "success" | "info" | "warn" | "error";

const BASE_TOAST_CLASS =
  "group toast rounded-xl border shadow-soft backdrop-blur-sm group-[.toaster]:text-foreground";

export const baseToastOptions: ToastOptions = {
  duration: 4000,
  classNames: {
    toast: `${BASE_TOAST_CLASS} group-[.toaster]:border-border group-[.toaster]:bg-card`,
    title: "text-sm font-medium text-foreground",
    description: "text-xs text-muted-foreground",
    icon: "mt-0.5",
    actionButton:
      "group-[.toast]:rounded-full group-[.toast]:bg-secondary group-[.toast]:px-3 group-[.toast]:text-foreground hover:group-[.toast]:bg-secondary/80",
    cancelButton:
      "group-[.toast]:rounded-full group-[.toast]:border group-[.toast]:border-border group-[.toast]:bg-transparent group-[.toast]:px-3 group-[.toast]:text-foreground/80",
  },
};

const VARIANT_OPTIONS: Record<NotificationVariant, ToastOptions> = {
  success: {
    classNames: {
      toast: "group-[.toaster]:border-success/30 group-[.toaster]:bg-success/10",
      icon: "text-success",
    },
  },
  info: {
    classNames: {
      toast: "group-[.toaster]:border-primary/30 group-[.toaster]:bg-background",
      icon: "text-primary",
    },
  },
  warn: {
    classNames: {
      toast: "group-[.toaster]:border-warning/40 group-[.toaster]:bg-warning/15",
      icon: "text-warning",
    },
  },
  error: {
    classNames: {
      toast: "group-[.toaster]:border-destructive/40 group-[.toaster]:bg-destructive/10",
      icon: "text-destructive",
    },
  },
};

export const toastIcons: ToastIcons = {
  info: <Info className="text-primary h-4 w-4" />,
  success: <CheckCircle2 className="text-success h-4 w-4" />,
  warning: <AlertTriangle className="text-warning h-4 w-4" />,
  error: <AlertCircle className="text-destructive h-4 w-4" />,
};

export const mergeToastOptions = (
  defaultOptions?: ToastOptions,
  overrideOptions?: ToastOptions,
): ToastOptions => {
  if (!defaultOptions && !overrideOptions) {
    return undefined;
  }

  if (!defaultOptions) {
    return overrideOptions;
  }

  if (!overrideOptions) {
    return defaultOptions;
  }

  return {
    ...defaultOptions,
    ...overrideOptions,
    classNames: mergeClassNames(defaultOptions.classNames, overrideOptions.classNames),
  };
};

const getVariantToastOptions = (variant: NotificationVariant, options?: ToastOptions) =>
  mergeToastOptions(mergeToastOptions(baseToastOptions, VARIANT_OPTIONS[variant]), options);

const baseSuccess = sonnerToast.success.bind(sonnerToast);
const baseError = sonnerToast.error.bind(sonnerToast);
const baseInfo = sonnerToast.info.bind(sonnerToast);
const baseWarning = sonnerToast.warning.bind(sonnerToast);

export const toast = {
  success: (message: ToastMessage, options?: ToastOptions) =>
    baseSuccess(message, getVariantToastOptions("success", options)),
  info: (message: ToastMessage, options?: ToastOptions) =>
    baseInfo(message, getVariantToastOptions("info", options)),
  warn: (message: ToastMessage, options?: ToastOptions) =>
    baseWarning(message, getVariantToastOptions("warn", options)),
  error: (message: ToastMessage, options?: ToastOptions) =>
    baseError(message, getVariantToastOptions("error", options)),
} as const;

function mergeClassNames(
  defaultClassNames?: ToastClassnames,
  overrideClassNames?: ToastClassnames,
): ToastClassnames | undefined {
  if (!defaultClassNames && !overrideClassNames) {
    return undefined;
  }

  if (!defaultClassNames) {
    return overrideClassNames;
  }

  if (!overrideClassNames) {
    return defaultClassNames;
  }

  const merged: ToastClassnames = { ...defaultClassNames };
  const keys = Object.keys(overrideClassNames) as Array<keyof ToastClassnames>;

  keys.forEach((key) => {
    const value = overrideClassNames[key];

    if (value === undefined || value === "") {
      return;
    }

    const currentValue = merged[key];
    merged[key] = currentValue ? `${currentValue} ${value}` : value;
  });

  return merged;
}
